import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import * as courseService from '../../services/course.service';
import { formatDuration, formatTimeRemaining } from '../../utils/duration';
import { groupByCategory } from '../../utils/courseCategories';

export default function LearningLab() {
  const [courses, setCourses] = useState(null);
  const [error, setError]     = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await courseService.listCourses();
      setCourses(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the Learning Lab right now.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCourse = (course) => {
    if (course.coming_soon || course.locked || course.lesson_count === 0) return;
    const lessonId = course.next_lesson?.id || course.first_lesson_id;
    if (!lessonId) return;
    navigate(`/dashboard/learning-lab/${course.id}/${lessonId}`);
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!courses) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto text-sm text-gray-400">Loading your courses…</div>
      </DashboardLayout>
    );
  }

  const macroCourses = courses.filter(c => c.type === 'macro');
  const microCourses = courses.filter(c => c.type === 'micro');
  const categoryGroups = groupByCategory(microCourses);

  const resumeCourse = [...courses]
    .filter(c => c.last_accessed_at && !c.locked && !c.coming_soon)
    .sort((a, b) => new Date(b.last_accessed_at) - new Date(a.last_accessed_at))[0];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">

        <div className="mb-6">
          <PageHeading title="Learning Lab" subtitle="Your 365reset courses — pick up right where you left off." />
        </div>

        {resumeCourse && <ResumeWidget course={resumeCourse} onOpen={openCourse} />}

        {courses.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center">
            <img src="/learning-lab-icon.png" alt="" className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm font-semibold text-reset-brown">Courses are on their way</p>
            <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
              We're moving the Learning Lab content over — check back soon.
            </p>
          </div>
        )}

        {macroCourses.length > 0 && (
          <section className="mb-10">
            <h2 className="text-base font-bold text-reset-brown mb-1">Your Reset Path</h2>
            <p className="text-xs text-gray-500 mb-4">Work through these in order — each one builds on the last.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {macroCourses.map(course => (
                <CourseCard key={course.id} course={course} onOpen={openCourse} isCore />
              ))}
            </div>
          </section>
        )}

        {categoryGroups.map(group => (
          <section key={group.name} className="mb-10">
            <h2 className="text-base font-bold text-reset-brown mb-1">{group.name}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {group.courses.map(course => (
                <CourseCard key={`${group.name}-${course.id}`} course={course} onOpen={openCourse} />
              ))}
            </div>
          </section>
        ))}

      </div>
    </DashboardLayout>
  );
}

// ─── Resume widget ────────────────────────────────────────────────────────

function ResumeWidget({ course, onOpen }) {
  if (!course.next_lesson) return null; // fully completed — nothing to resume into

  return (
    <button
      onClick={() => onOpen(course)}
      className="w-full text-left bg-reset-brown rounded-2xl shadow-sm px-5 py-4 mb-8 flex items-center justify-between gap-4 hover:bg-reset-brown/90 transition-colors"
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-white/50 uppercase tracking-wide">Continue where you left off</p>
        <p className="text-base font-bold text-white mt-1 truncate">{course.title}</p>
        <p className="text-sm text-white/70 mt-0.5 truncate">
          Next: {course.next_lesson.title}
          {course.next_lesson.video_duration_seconds > 0 && (
            <span className="text-white/50"> · {formatDuration(course.next_lesson.video_duration_seconds)}</span>
          )}
        </p>
      </div>
      <span className="shrink-0 px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl">
        Resume
      </span>
    </button>
  );
}

// ─── Course card ──────────────────────────────────────────────────────────

function CourseCard({ course, onOpen, isCore }) {
  // Coming soon (Jenn, 2026-10-08): just the picture, the title and a label —
  // nothing to open until the course is published.
  if (course.coming_soon) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
        <div className={`aspect-video flex items-center justify-center text-4xl ${isCore ? 'bg-reset-terracotta/10' : 'bg-reset-sage/10'}`}>
          {course.cover_image_url
            ? <img src={course.cover_image_url} alt="" className="w-full h-full object-cover" />
            : '📘'}
        </div>
        <div className="p-4 flex flex-col gap-2 flex-1">
          <h3 className="text-sm font-bold text-reset-brown leading-snug">{course.title}</h3>
          <span className="mt-auto self-start text-[11px] font-medium px-2.5 py-1 rounded-full bg-reset-gold/30 text-reset-brown">
            Coming soon
          </span>
        </div>
      </div>
    );
  }

  const pct = course.lesson_count > 0
    ? Math.round((course.completed_count / course.lesson_count) * 100)
    : 0;
  const timeLabel = formatTimeRemaining(course.remaining_duration_seconds, course.completed_count, course.lesson_count);
  const totalLabel = formatDuration(course.total_duration_seconds);
  const noContent = course.lesson_count === 0;

  return (
    <div className={`bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col ${course.locked ? 'opacity-60' : ''}`}>

      {/* Cover */}
      <div className={`aspect-video flex items-center justify-center text-4xl ${
        isCore ? 'bg-reset-terracotta/10' : 'bg-reset-sage/10'
      }`}>
        {course.cover_image_url
          ? <img src={course.cover_image_url} alt="" className="w-full h-full object-cover" />
          : (course.locked ? '🔒' : '📘')}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-sm font-bold text-reset-brown leading-snug">{course.title}</h3>
        {course.instructor_name && <p className="text-xs font-semibold text-reset-sage">with {course.instructor_name}</p>}
        {course.description && (
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{course.description}</p>
        )}

        {!noContent && (
          <div className="mt-1">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-reset-terracotta rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-gray-400 mt-1">
              <span>{pct}% complete</span>
              {timeLabel && <span>{timeLabel}{totalLabel ? ` of ${totalLabel}` : ''}</span>}
            </div>
          </div>
        )}

        <button
          onClick={() => onOpen(course)}
          disabled={course.locked || noContent}
          className={`mt-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
            course.locked || noContent
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-reset-terracotta text-white hover:bg-reset-brown'
          }`}
        >
          {course.locked ? 'Locked' : noContent ? 'Coming soon' : course.completed_count > 0 ? 'Continue' : 'Start'}
        </button>
      </div>
    </div>
  );
}
